import { makeAutoObservable } from "mobx";
import { ConfigApi } from "../api/base/ConfigApi";
import CommonApiClient from "../api/common/CommonApiClient";
import EmployeesApiClient from "../api/EmployeesApiClient";
import CompanyApiClient from "../api/CompanyApiClient";
import ProjectsApiClient from "../api/ProjectsApiClient";
import ProjectDocumentsApiClient from "../api/ProjectDocumentsApiClient";
import TimeReportsApiClient from "../api/TimeReportsApiClient";
import EventsApiClient from "../api/EventsApiClient";
import NotificationsApiClient from "../api/NotificationsApiClient";
import TasksApiClient from "../api/TasksApiClient";
import SalaryApiClient from "../api/SalaryApiClient";
import TariffPlansApiClient from "../api/TariffPlansApiClient";
import PaymentApiClient from "../api/PaymentApiClient";
import TestsApiClient from "../api/TestsApiClient";
import UserResourceApi from "../api/UserResourceApi";
import TestAttemptApiClient from "../api/TestAttemptApiClient";
import KanbanTaskApiClient from "../api/KanbanTaskApiClient";
import { AUTHENTICATED_ROUTES } from "../api/kanban/kanbanRoutes";
import type { KanbanRoutes } from "../api/kanban/kanbanRoutes";
import ProjectFilesApiClient from "../api/ProjectFilesApiClient";
import { CurrencyApiClient, FinancialTransactionApiClient, OperationTypeApiClient, PaymentTypeApiClient } from "../api/FinanceApiClient";
import CRMApiClient from "../api/CRMApiClient";
import EmailApiClient from "../api/EmailApiClient";
import ProjectEventsApiClient from "../api/ProjectEventsApiClient";
import TicketApi from "../api/TicketApi";
import GoogleCalendarApiClient from "../api/GoogleCalendarApiClient";
import TeamsCalendarApiClient from "../api/TeamsCalendarApiClient";
import AnonymousApiClient from "../api/AnonymousApiClient";
import { th } from "date-fns/locale";
import FavoriteProjectApiClient from "../api/FavoriteProjectApiClient";
import ProjectDocumentsFoldersApiClient from "../api/ProjectDocumentsFoldersApiClient";
import CountryApiClient from "../api/CountryApiClient";
import CountryPaymentSystemApiClient from "../api/CountryPaymentSystemApiClient";
import CurrencyExchangeRateApiClient from "../api/CurrencyExchangeRateApiClient";

class ApiStore {
  CommonApiClient!: CommonApiClient;
  EmployeesApiClient!: EmployeesApiClient;
  CompanyApiClient!: CompanyApiClient;
  ProjectsApiClient!: ProjectsApiClient;
  AnonymousApiClient!: AnonymousApiClient;
  ProjectDocumentsApiClient!: ProjectDocumentsApiClient;
  ProjectDocumentsFoldersApiClient!: ProjectDocumentsFoldersApiClient;
  TimeReportsApiClient!: TimeReportsApiClient;
  EventsApiClient!: EventsApiClient;
  NotificationsApiClient!: NotificationsApiClient;
  TasksApiClient!: TasksApiClient;
  SalaryApiClient!: SalaryApiClient;
  TariffPlansApiClient!: TariffPlansApiClient; 
  PaymentApiClient!: PaymentApiClient;
  CountryPaymentSystemApiClient!: CountryPaymentSystemApiClient;
  TestsApiClient!: TestsApiClient;
  TestAttemptApiClient!: TestAttemptApiClient;
  UserResourceApi!: UserResourceApi;
  KanbanTaskApiClient!: KanbanTaskApiClient;
  ProjectFilesApiClient!: ProjectFilesApiClient;
  CurrencyApiClient!: CurrencyApiClient;
  OperationTypeApiClient!: OperationTypeApiClient;
  PaymentTypeApiClient!: PaymentTypeApiClient;
  FinancialTransactionApiClient!: FinancialTransactionApiClient;
  EmailApiClient!: EmailApiClient;
  CRMApiClient!: CRMApiClient;
  ProjectEventsApiClient!: ProjectEventsApiClient;
  CountryApiClient!: CountryApiClient;
  // GoogleCalendarApiClient!: GoogleCalendarApiClient;
  // TeamsCalendarApiClient!: TeamsCalendarApiClient;
  TicketApi!: TicketApi;
  FavoriteProjectApiClient!: FavoriteProjectApiClient;
  CurrencyExchangeRateApiClient!: CurrencyExchangeRateApiClient;
  
  configApi: ConfigApi = {} as ConfigApi;

  /** Route table of the kanban client; survives `setConfigApi` so a token refresh keeps the mode. */
  kanbanRoutes: KanbanRoutes = AUTHENTICATED_ROUTES;

  constructor() {
    makeAutoObservable(this);
    this.CommonApiClient = new CommonApiClient({} as ConfigApi);
  }

  setConfigApi = (configApi: ConfigApi) => {
    this.configApi = configApi;
    this.CommonApiClient = new CommonApiClient(configApi);
    this.EmployeesApiClient = new EmployeesApiClient(configApi);
    this.CompanyApiClient = new CompanyApiClient(configApi);
    this.ProjectsApiClient = new ProjectsApiClient(configApi);
    this.AnonymousApiClient = new AnonymousApiClient(configApi);
    this.ProjectDocumentsApiClient = new ProjectDocumentsApiClient(configApi);
    this.ProjectDocumentsFoldersApiClient = new ProjectDocumentsFoldersApiClient(configApi);
    this.TimeReportsApiClient = new TimeReportsApiClient(configApi);
    this.EventsApiClient = new EventsApiClient(configApi);
    this.SalaryApiClient = new SalaryApiClient(configApi);
    this.NotificationsApiClient = new NotificationsApiClient(configApi);
    this.TasksApiClient = new TasksApiClient(configApi);
    this.TariffPlansApiClient = new TariffPlansApiClient(configApi); 
    this.PaymentApiClient = new PaymentApiClient(configApi); 
    this.CountryPaymentSystemApiClient = new CountryPaymentSystemApiClient(configApi);
    this.TestsApiClient = new TestsApiClient(configApi); 
    this.TestAttemptApiClient = new TestAttemptApiClient(configApi); 
    this.UserResourceApi = new UserResourceApi(configApi); 
    this.KanbanTaskApiClient = new KanbanTaskApiClient(configApi, this.kanbanRoutes);
    this.ProjectFilesApiClient = new ProjectFilesApiClient(configApi);
    this.CurrencyApiClient = new CurrencyApiClient(configApi);
    this.OperationTypeApiClient = new OperationTypeApiClient(configApi);
    this.PaymentTypeApiClient = new PaymentTypeApiClient(configApi);
    this.FinancialTransactionApiClient = new FinancialTransactionApiClient(configApi);
    this.CRMApiClient = new CRMApiClient(configApi);
    this.EmailApiClient = new EmailApiClient(configApi);
    this.ProjectEventsApiClient = new ProjectEventsApiClient(configApi);
    // this.GoogleCalendarApiClient = new GoogleCalendarApiClient(configApi);
    // this.TeamsCalendarApiClient  = new TeamsCalendarApiClient(configApi);
    this.TicketApi = new TicketApi(configApi);
    this.FavoriteProjectApiClient = new FavoriteProjectApiClient(configApi);
    this.CountryApiClient = new CountryApiClient(configApi);
    this.CurrencyExchangeRateApiClient = new CurrencyExchangeRateApiClient(configApi);
  };

  /** Points the kanban client at the authenticated or the public route table (live client + future re-creations). */
  setKanbanRoutes = (routes: KanbanRoutes) => {
    this.kanbanRoutes = routes;
    this.KanbanTaskApiClient?.setRoutes(routes);
  };
}
export default ApiStore;
